import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';

const SettingsContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.primary};
  border-bottom: 1px solid ${props => props.theme.colors.primary};
  padding-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }
`;

const Section = styled.div`
  background-color: #0a0a0a;
  border-radius: 4px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #222;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    
    & > *:last-child {
      align-self: flex-end;
    }
  }
`;

const Label = styled.span`
  opacity: 0.7;
`;

const Button = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: #000;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.accent};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleSwitch = styled.div<{ active: boolean }>`
  width: 50px;
  height: 24px;
  background-color: ${props => props.active ? props.theme.colors.primary : '#333'};
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '28px' : '2px'};
    width: 20px;
    height: 20px;
    background-color: #fff;
    border-radius: 50%;
    transition: all 0.3s ease;
  }
`;

const Select = styled.select`
  background-color: #161616;
  border: 1px solid #333;
  color: ${props => props.theme.colors.text};
  padding: 0.5rem;
  border-radius: 4px;
  min-width: 150px;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    outline: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Option = styled.option`
  background-color: #161616;
  color: ${props => props.theme.colors.text};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  margin-top: 0.5rem;
  background-color: rgba(255, 85, 85, 0.1);
  padding: 0.5rem;
  border-radius: 4px;
`;

const Success = styled.div`
  color: ${props => props.theme.colors.success};
  margin-top: 0.5rem;
  background-color: rgba(85, 255, 127, 0.1);
  padding: 0.5rem;
  border-radius: 4px;
`;

const SettingsPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState(true);
  const [autoTrade, setAutoTrade] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [network, setNetwork] = useState('mainnet');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSaveSettings = () => {
    // Simulate saving settings
    setSuccess('Settings saved successfully!');
    setTimeout(() => setSuccess(null), 3000);
  };
  
  return (
    <DashboardLayout activeMenuItem="settings">
      <SettingsContainer>
        <Title>Settings</Title>
        
        <Section>
          <SectionTitle>General Settings</SectionTitle>
          
          <SettingRow>
            <Label>Enable Notifications</Label>
            <ToggleSwitch 
              active={notifications} 
              onClick={() => setNotifications(!notifications)} 
            />
          </SettingRow>
          
          <SettingRow>
            <Label>Auto-Trade</Label>
            <ToggleSwitch 
              active={autoTrade} 
              onClick={() => setAutoTrade(!autoTrade)} 
            />
          </SettingRow>
          
          <SettingRow>
            <Label>Theme</Label>
            <Select 
              value={theme} 
              onChange={(e) => setTheme(e.target.value)}
            >
              <Option value="dark">Dark</Option>
              <Option value="light">Light</Option>
              <Option value="system">System Default</Option>
            </Select>
          </SettingRow>
        </Section>
        
        <Section>
          <SectionTitle>Network Settings</SectionTitle>
          
          <SettingRow>
            <Label>Network</Label>
            <Select 
              value={network} 
              onChange={(e) => setNetwork(e.target.value)}
            >
              <Option value="mainnet">Mainnet</Option>
              <Option value="devnet">Devnet</Option>
              <Option value="testnet">Testnet</Option>
            </Select>
          </SettingRow>
        </Section>
        
        <Button onClick={handleSaveSettings}>Save Settings</Button>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <Success>{success}</Success>}
      </SettingsContainer>
    </DashboardLayout>
  );
};

export default SettingsPage; 